/**
 * Supplement query hooks
 * 
 * Handles all supplement data fetching and transformation.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { apiClient } from '@/lib/api-client';
import { SupplementDetailed } from '@/api/generated/client/models';
import { transformSupplementForDisplay } from './useSupplementTransform';
import { useToast } from '@/hooks/use-toast';
import { useSupplementGenerationSSE } from './useSSEProgress';

/**
 * Query hook for fetching a list of supplements for a claim with infinite scrolling support
 */
export function useSupplements(claimId: number, limit: number = 5) {
  const queryClient = useQueryClient();

  const infiniteQueryResult = useInfiniteQuery({
    queryKey: ['claim', claimId, 'supplements'],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiClient.supplements.listSupplements({
        claimId,
        page: (pageParam as number),
        limit,
      });
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { page, total, limit } = lastPage;
      if (page === undefined || total === undefined || limit === undefined) return undefined;
      const totalPages = Math.ceil(total / limit);
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!claimId,
  });

  const supplements = useMemo(() => {
    if (!infiniteQueryResult.data) return [];
    return infiniteQueryResult.data.pages.flatMap((page) => page.items || []);
  }, [infiniteQueryResult.data]);

  const createSupplementMutation = useMutation({
    mutationFn: async (data: { claimId: number; comparisonId: number; state?: string | null }) => {
      return await apiClient.supplements.createSupplement({
        createSupplementRequest: {
          claimId: data.claimId,
          comparisonId: data.comparisonId,
          state: data.state
        } as any,
      });
    },
    onSuccess: () => {
      // Invalidate supplements list to refetch
      queryClient.invalidateQueries({ queryKey: ['claim', claimId, 'supplements'] });
      // Invalidate notifications to show new activity in bell
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    ...infiniteQueryResult,
    supplements,
    totalCount: infiniteQueryResult.data?.pages[0]?.total || 0,
    createSupplementAsync: createSupplementMutation.mutateAsync,
    isCreating: createSupplementMutation.isPending,
  };
}

export function useDeleteSupplement(claimId: number) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (supplementId: number) => {
      await apiClient.supplements.deleteSupplement({ supplementId });
      return supplementId;
    },
    onSuccess: (_data, supplementId) => {
      queryClient.invalidateQueries({ queryKey: ['claim', claimId, 'supplements'] });
      queryClient.removeQueries({ queryKey: ['supplement', supplementId] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to delete supplement', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * Query hook for fetching a single supplement with full details
 */
export function useSupplement(supplementId: number | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['supplement', supplementId],
    queryFn: async () => {
      if (!supplementId) throw new Error('Supplement ID required');
      return await apiClient.supplements.getSupplement({
        supplementId,
      });
    },
    enabled: !!supplementId,
  });

  const supplement = query.data;

  // Determine if we should connect to SSE
  const isCompleted = supplement?.workflowStatus === 'completed';
  const isFailed = supplement?.workflowStatus === 'failed';
  const isActive = !isCompleted && !isFailed;

  // Monitor SSE for completion/failure and invalidate queries
  const { isComplete: sseComplete, isFailed: sseFailed } = useSupplementGenerationSSE(
    supplementId,
    isActive
  );

  useEffect(() => {
    if (sseComplete || sseFailed) {
      // Invalidate supplement queries to refresh data
      if (supplementId && supplement?.claimId) {
        queryClient.invalidateQueries({ queryKey: ['claim', supplement.claimId, 'supplements'] });
      }
      if (supplementId) {
        queryClient.invalidateQueries({ queryKey: ['supplement', supplementId] });
      }
      // Invalidate notifications to show completion notification immediately in bell
      // (Backend publishes activity event, but this ensures instant UI update)
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  }, [sseComplete, sseFailed, supplementId, supplement?.claimId, queryClient]);

  return query;
}

/**
 * Hook that fetches supplement and returns transformed display data
 * This is the primary hook for the supplement UI
 */
export function useSupplementData(supplementId: number | null) {
  const { data: supplement, ...queryResult } = useSupplement(supplementId);

  const transformedData = useMemo(() => {
    if (!supplement) {
      return {
        rooms: [],
        totals: {
          totalSupplementCents: 0,
          unmatchedContractorCount: 0,
          underscopedCount: 0,
          overscopedCount: 0,
          unmatchedInsurerCount: 0,
          perfectMatchCount: 0
        },
      };
    }
    return transformSupplementForDisplay(supplement as SupplementDetailed);
  }, [supplement]);

  return {
    ...queryResult,
    supplement,
    rooms: transformedData.rooms,
    totals: transformedData.totals,
  };
}

/**
 * Hook for supplement PDF export with complete download functionality
 * Encapsulates the PDF export logic including:
 * - Triggering PDF generation via Lambda
 * - Handling cached vs. generated PDFs
 * - Automatic file download
 * - Toast notifications for success/error/progress
 * 
 * The mutation key ['supplements', 'exportPdf'] is used by usePdfSSE to track active PDF exports
 */
export function useSupplementPdfExport() {
  const { toast } = useToast();
  const pdfExport = useMutation({
    mutationKey: ['supplements', 'exportPdf'],
    mutationFn: async (supplementId: number) => {
      return apiClient.supplements.exportSupplementPdf({ supplementId });
    },
  });

  const handlePdfExport = (supplementId: number) => {
    pdfExport.mutate(supplementId, {
      onSuccess: (result) => {
        // Check if we got a download URL immediately (cached PDF)
        if (result.downloadUrl) {
          toast({ title: 'PDF is ready!' });
          // Trigger download without opening in new tab
          // Fetch the PDF as a blob to force download behavior
          fetch(result.downloadUrl)
            .then(response => response.blob())
            .then(blob => {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `supplement-${supplementId}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            })
            .catch(err => {
              console.error('Failed to download PDF:', err);
              // Fallback to opening in new tab if fetch fails
              if (result.downloadUrl) {
                window.open(result.downloadUrl, '_blank');
              }
            });
        } else {
          // PDF is being generated - SSE connection will notify when ready
          toast({
            title: 'PDF generation started. You will be notified when it\'s ready.'
          });
        }
      },
      onError: (error) => {
        console.error('PDF export error:', error);
        const errorMessage = error instanceof Error
          ? error.message
          : 'Failed to generate PDF';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive'
        });
      },
    });
  };

  return {
    handlePdfExport,
    isPending: pdfExport.isPending,
  };
}

/**
 * Hook for supplement Excel export with complete download functionality
 * Encapsulates the Excel export logic including:
 * - Triggering Excel generation
 * - Automatic file download
 * - Toast notifications for success/error
 */
export function useSupplementExcelExport() {
  const { toast } = useToast();
  const excelExport = useMutation({
    mutationKey: ['supplements', 'exportExcel'],
    mutationFn: async (supplementId: number) => {
      return apiClient.supplements.exportSupplementExcel({ supplementId });
    },
  });

  const handleExcelExport = (supplementId: number) => {
    excelExport.mutate(supplementId, {
      onSuccess: (blob) => {
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `supplement-${supplementId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Excel export successful',
          description: 'Your supplement has been downloaded.'
        });
      },
      onError: (error) => {
        console.error('Excel export error:', error);
        const errorMessage = error instanceof Error
          ? error.message
          : 'Failed to export Excel';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive'
        });
      },
    });
  };

  return {
    handleExcelExport,
    isPending: excelExport.isPending,
  };
}
